import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, semantic, radius } from '@/shared/theme/tokens';

/**
 * Top of the control-room screen. Two bands:
 *   1. identity row — plant name, LIVE/DEMO badge, data-freshness chip, wall clock
 *   2. the adaptive broadcast headline — the single most important real estate,
 *      one unambiguous state readable from across the room.
 */
export function StatusBar({ deploymentName, clock, jitterActive, animate, headline, freshness }) {
  const c = useThemeColors();
  const isDark = useIsDark();

  const time = clock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = clock.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      padding: '10px 16px 12px',
      borderRadius: radius.xl,
      background: isDark ? 'linear-gradient(90deg, #161b29, #1a2235)' : 'linear-gradient(90deg, #ffffff, #f3f6fc)',
      border: `1px solid ${c.border}`,
      boxShadow: isDark ? 'none' : '0 2px 6px rgba(30,67,150,0.05)',
      flexShrink: 0,
    }}>
      {/* Identity row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* LIVE / DEMO badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            position: 'relative', width: 10, height: 10, borderRadius: '50%',
            background: jitterActive ? semantic.successAlt : semantic.error,
            boxShadow: `0 0 0 3px ${jitterActive ? 'rgba(16,185,129,0.25)' : 'rgba(220,38,38,0.25)'}`,
            animation: animate ? 'lm-pulse 1.6s ease-in-out infinite' : 'none',
          }} />
          <span style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.wider,
            color: jitterActive ? semantic.successAlt : semantic.error,
          }}>
            {jitterActive ? 'DEMO' : 'LIVE'}
          </span>
        </div>

        <div style={{ width: 1, height: 22, background: c.border, flexShrink: 0 }} />

        {/* Plant name */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {deploymentName || 'Control Room'}
          </span>
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>
            Operator Live Mode
          </span>
        </div>

        {/* Data-freshness chip — proves the screen knows how current it is */}
        {freshness && <FreshnessChip freshness={freshness} animate={animate} c={c} isDark={isDark} />}

        <div style={{ width: 1, height: 22, background: c.border, flexShrink: 0 }} />

        {/* Wall clock */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
          <span style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
          }}>
            {time}
          </span>
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{date}</span>
        </div>
      </div>

      {/* Adaptive broadcast headline */}
      {headline && <Headline headline={headline} animate={animate} isDark={isDark} c={c} />}
    </div>
  );
}

function Headline({ headline, animate, isDark, c }) {
  const bg = isDark ? headline.bgDark : headline.bgLight;
  const showCount = headline.count > 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 18px', borderRadius: radius.lg,
      background: bg, border: `1px solid ${headline.border}`,
      borderLeft: `5px solid ${headline.color}`,
    }}>
      <span className="material-symbols-outlined" style={{
        fontSize: 32, color: headline.color, flexShrink: 0,
        animation: animate && (headline.state === 'critical' || headline.state === 'degraded') ? 'lm-pulse 1.4s ease-in-out infinite' : 'none',
      }}>
        {headline.icon}
      </span>
      {showCount && (
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.extrabold,
          color: headline.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {headline.count}
        </span>
      )}
      <span style={{
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.extrabold,
        letterSpacing: typography.letterSpacing.wide, color: headline.color, lineHeight: 1.05,
      }}>
        {headline.label}
      </span>
      {headline.subtext && (
        <span style={{ marginLeft: 'auto', fontSize: typography.fontSize.sm, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
          {headline.subtext}
        </span>
      )}
    </div>
  );
}

function FreshnessChip({ freshness, animate, c, isDark }) {
  const { stale, ageSeconds } = freshness;
  const meta = stale
    ? { color: '#0891b2', label: 'DATA STALE' }
    : { color: semantic.successAlt, label: 'DATA LIVE' };
  const ageLabel = ageSeconds < 2 ? 'just now' : `${ageSeconds}s ago`;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
      padding: '5px 10px', borderRadius: radius.full,
      background: isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle,
      border: `1px solid ${c.border}`,
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: meta.color,
        animation: animate && !stale ? 'lm-pulse 1.8s ease-in-out infinite' : 'none',
      }} />
      <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: meta.color, letterSpacing: typography.letterSpacing.wide }}>
        {meta.label}
      </span>
      <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
        updated {ageLabel}
      </span>
    </div>
  );
}
